import { useState, type ComponentType } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Check, Globe, Loader2, MessageSquare, Phone } from 'lucide-react';
import { usePlatformIntegrations } from '@/features/integrations/hooks/usePlatformIntegrations';
import { WebChatEmbedCard } from '@/features/integrations/components/WebChatEmbedCard';
import { StepFooter } from './StepFooter';

// Where Meta's OAuth sends the new tab back to. A path, not a query string: the callback appends
// its own query parameters, which would corrupt a "?step=" value.
const RETURN_TO = '/onboarding/channel';

interface ChannelStepProps {
  connected: boolean;
  hasBusiness: boolean;
  onDone: () => void;
}

export function ChannelStep({ connected, hasBusiness, onDone }: ChannelStepProps) {
  const { connectFacebook, connectInstagram, isConnectingFacebook, isConnectingInstagram } =
    usePlatformIntegrations();
  const [showWebChat, setShowWebChat] = useState(false);

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Connect where your customers message you</h2>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        Connect at least one channel so the AI can start replying. Connecting opens in a new tab; come
        back here when you are done. You can add more channels later in Integrations.
      </p>

      {connected && (
        <div className="mt-4 flex items-center gap-2 rounded-md bg-emerald-50 dark:bg-emerald-950/40 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60">
          <Check className="h-4 w-4" aria-hidden="true" />
          A channel is connected.
        </div>
      )}

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <ChannelOption
          icon={MessageSquare}
          label="Facebook Messenger"
          hint="Connect a Facebook Page"
          busy={isConnectingFacebook}
          onClick={() => connectFacebook(RETURN_TO)}
        />
        <ChannelOption
          icon={Camera}
          label="Instagram"
          hint="Business or Creator account"
          busy={isConnectingInstagram}
          onClick={() => connectInstagram(RETURN_TO)}
        />
        <Link
          to="/integrations"
          className="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-800 p-4 text-left transition-colors hover:border-primary/50 hover:bg-slate-50 dark:hover:bg-slate-900/50"
        >
          <Phone className="mt-0.5 h-5 w-5 text-primary" aria-hidden="true" />
          <span>
            <span className="block text-sm font-medium text-slate-900 dark:text-white">WhatsApp Business</span>
            <span className="block text-xs text-slate-500 dark:text-slate-400">Needs your WhatsApp API details: set up in Integrations</span>
          </span>
        </Link>
        <ChannelOption
          icon={Globe}
          label="Website chat"
          hint="A chat bubble for your own website"
          busy={false}
          onClick={() => setShowWebChat((v) => !v)}
        />
      </div>

      {showWebChat && (
        <div className="mt-4">
          {hasBusiness ? (
            <WebChatEmbedCard />
          ) : (
            <p className="rounded-md bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60">
              Add your business details first (step 1): the widget greets visitors by your business name.
            </p>
          )}
        </div>
      )}

      <StepFooter onSkip={onDone} onPrimary={onDone} />
    </div>
  );
}

interface ChannelOptionProps {
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>;
  label: string;
  hint: string;
  busy: boolean;
  onClick: () => void;
}

function ChannelOption({ icon: Icon, label, hint, busy, onClick }: ChannelOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-800 p-4 text-left transition-colors hover:border-primary/50 hover:bg-slate-50 dark:hover:bg-slate-900/50 disabled:opacity-60"
    >
      {busy ? (
        <Loader2 className="mt-0.5 h-5 w-5 animate-spin text-primary" aria-hidden="true" />
      ) : (
        <Icon className="mt-0.5 h-5 w-5 text-primary" aria-hidden="true" />
      )}
      <span>
        <span className="block text-sm font-medium text-slate-900 dark:text-white">{label}</span>
        <span className="block text-xs text-slate-500 dark:text-slate-400">{hint}</span>
      </span>
    </button>
  );
}
